// Single source of truth for how chart context is phrased and colored across
// the client portal (persistent chip, compose "Regarding:" header, participant
// permission banner). The permission labels themselves (role + share scope)
// come from the backend disclosure_scope helper — the SAME PatientRelationship
// flags the Case "Contacts & Permissions" card reads — so this file only
// formats strings and picks colors; it never derives permissions.

export interface ChartContext {
  patientId: string | null;
  name: string; // chart owner (the person the chart is about)
  viewerName: string; // the logged-in person viewing it
  roleLabel: string; // e.g. "Parent/Guardian", "Primary patient"
  shareScope: string; // e.g. "All info", "Billing & Scheduling only"
  relativeLabel: string; // e.g. "my child", "self", "my partner"
  isSelf: boolean;
}

// Deterministic, stable palette so a given chart keeps the same color across
// pages. Self chart is always the calm/neutral primary tone.
const CHART_PALETTE = [
  { main: '#2e7d32', bg: '#e8f5e9', text: '#1b5e20' }, // green
  { main: '#6a1b9a', bg: '#f3e5f5', text: '#4a148c' }, // purple
  { main: '#c62828', bg: '#ffebee', text: '#b71c1c' }, // red
  { main: '#ef6c00', bg: '#fff3e0', text: '#e65100' }, // orange
  { main: '#00838f', bg: '#e0f7fa', text: '#006064' }, // teal
  { main: '#ad1457', bg: '#fce4ec', text: '#880e4f' }, // pink
];

const SELF_COLOR = { main: '#1565c0', bg: '#e3f2fd', text: '#0d47a1' }; // blue

// Neutral slate tone for the aggregate/household chip (no chart drilled in), so
// it reads as "all charts" rather than any one person's color.
export const AGGREGATE_COLOR = { main: '#455a64', bg: '#eceff1', text: '#263238' };

// Aggregate/household chip copy (multi-chart, no active chart).
export const AGGREGATE_LABEL = 'Household — all charts';
export const AGGREGATE_LABEL_SHORT = 'All charts';

export interface ChartColor {
  main: string;
  bg: string;
  text: string;
}

export function chartColor(ctx: { isSelf: boolean; patientId: string | null }): ChartColor {
  if (ctx.isSelf || !ctx.patientId) return SELF_COLOR;
  // Hash the patient id to a stable palette slot.
  let hash = 0;
  for (let i = 0; i < ctx.patientId.length; i += 1) {
    hash = (hash * 31 + ctx.patientId.charCodeAt(i)) & 0xffffffff;
  }
  return CHART_PALETTE[Math.abs(hash) % CHART_PALETTE.length];
}

// Persistent top-right chip:
//   self     -> "Viewing: Jane Williams chart as Jane Williams (self, All info)"
//   relative -> "Viewing: Taylor Williams chart as Jane Williams (parent/guardian, All info)"
export function viewingLabel(ctx: ChartContext): string {
  const role = ctx.isSelf ? 'self' : (ctx.roleLabel || 'linked').toLowerCase();
  const scope = ctx.shareScope || 'All info';
  return `Viewing: ${ctx.name} chart as ${ctx.viewerName} (${role}, ${scope})`;
}

// Compact chip for narrow / mobile: "Taylor's chart" (first name + possessive),
// or "My chart" for self.
export function chipLabel(ctx: ChartContext): string {
  if (ctx.isSelf) return 'My chart';
  const first = (ctx.name || '').split(' ')[0] || 'Linked';
  return `${first}'s chart`;
}

// Compose "Regarding:" header. Two-line for a relative, single line for self:
//   Regarding: Taylor Williams (my child)
//   Relationship: Parent · All info
//   ---
//   Regarding: Jane Williams (self)
export function regardingLines(ctx: ChartContext): { primary: string; secondary: string | null } {
  if (ctx.isSelf) {
    return { primary: `Regarding: ${ctx.name} (self)`, secondary: null };
  }
  const relative = ctx.relativeLabel || 'linked';
  const secondary = `Relationship: ${ctx.roleLabel || 'Linked'} · ${ctx.shareScope || 'All info'}`;
  return { primary: `Regarding: ${ctx.name} (${relative})`, secondary };
}

// Short thread label for the inbox list: "Regarding: Taylor (my child)".
export function threadRegardingLabel(about: {
  name: string;
  relativeLabel?: string | null;
  isSelf?: boolean;
}): string {
  if (!about.name) return '';
  if (about.isSelf) return `Regarding: ${about.name} (self)`;
  const relative = about.relativeLabel || 'linked';
  return `Regarding: ${about.name} (${relative})`;
}

// Who/Share tag for a single participant in the permission banner:
// "Jane (Parent/Guardian · All info)".
export function participantTag(p: {
  name: string;
  roleLabel?: string | null;
  shareScope?: string | null;
}): string {
  const parts = [p.roleLabel, p.shareScope].filter(Boolean).join(' · ');
  return parts ? `${p.name} (${parts})` : p.name;
}

// Role labels (from the backend role_label) that read as "parent/guardian" in
// the portal greeting. Every other role is named by its own label, so a partner
// is never greeted as a parent/guardian (PORTAL-11).
const GUARDIAN_GREETING_ROLES = new Set(['parent', 'guardian', 'legal guardian']);

/** Greeting suffix for the ACTIVE chart, driven by that chart's relationship
 * label — never by whichever other linked chart happens to be a parent link:
 *   guardian -> ", parent/guardian of Taylor Williams"
 *   partner  -> ", partner of Sam Williams"
 *   unknown  -> ", viewing Sam Williams" */
export function greetingRelationSuffix(
  ctx: Pick<ChartContext, 'name' | 'roleLabel' | 'isSelf'> | null,
  fallbackName?: string | null,
): string {
  if (ctx?.isSelf) return '';
  const name = ctx?.name || fallbackName || 'client';
  const role = (ctx?.roleLabel || '').trim().toLowerCase();
  if (GUARDIAN_GREETING_ROLES.has(role)) return `, parent/guardian of ${name}`;
  if (role && role !== 'linked' && role !== 'contact') return `, ${role} of ${name}`;
  return `, viewing ${name}`;
}
